import React, { useState, useRef, useEffect } from 'react';
import {
  Calendar,
  ClipboardList,
  UtensilsCrossed,
  CreditCard,
  Users,
  Link2,
  AlertCircle,
  Clock,
  Bell,
  ChevronDown,
  LayoutDashboard,
  ChefHat,
  FileText,
  Briefcase,
  Shield,
  LogOut,
  Check,
  Menu,
  X,
  Settings,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  Trash2,
  ListTodo,
  Inbox,
  BedDouble,
  Heart,
} from 'lucide-react';
import ToastContainer from './Toast';
import { useAppContext } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { ROLES, canAccessPage, getRoleColour } from '../lib/userSystem';
import UserManagement from '../pages/UserManagement';
import Dashboard from '../pages/Dashboard';
import Pipeline from '../pages/Pipeline';
import RoomDiary from '../pages/RoomDiary';
import Events from '../pages/Events';
import Tables from '../pages/Tables';
import FunctionSheets from '../pages/FunctionSheets';
import Billing from '../pages/Billing';
import Clients from '../pages/Clients';
import Email from '../pages/Email';
import Integrations from '../pages/Integrations';
import Marketing from '../pages/Marketing';
import BookingWidget from '../pages/BookingWidget';
import KitchenLive from '../pages/KitchenLive';
import HousekeepingBoard from '../pages/HousekeepingBoard';
import HousekeepingLive from '../pages/HousekeepingLive';
import WinYourWedding from '../pages/WinYourWedding';

const Layout = () => {
  // Direct links, e.g. https://errigal-events.vercel.app/?page=housekeeping
  // (page access is still checked below, so a link can't open a page the role can't see).
  const [activePage, setActivePage] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      if (p.get('wyw_ticket')) return 'wyw'; // Final Night ticket QR scanned at the door
      return p.get('page') || 'dashboard';
    } catch (_) { return 'dashboard'; }
  });
  const [navigateAction, setNavigateAction] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showOffice, setShowOffice] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState('');
  const [changePasswordSuccess, setChangePasswordSuccess] = useState('');
  const [showEmailQuickAccess, setShowEmailQuickAccess] = useState(false);
  const profileRef = useRef(null);
  const notifRef = useRef(null);
  const emailQuickRef = useRef(null);

  const {
    emailToasts, cancelWorkflowEmail, dismissToast,
    currentUser, switchUser, availableUsers,
    notifications, dismissNotification, deleteNotification, clearAllNotifications,
    dbLoading,
    unreadEmailCount,
  } = useAppContext();

  const { logout, isSupabaseConfigured, isAuthenticated, changePassword } = useAuth();
  const isRealAuth = isSupabaseConfigured && isAuthenticated;

  const role = currentUser?.role || 'reception';

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfileMenu(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifications(false);
      if (emailQuickRef.current && !emailQuickRef.current.contains(e.target)) setShowEmailQuickAccess(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChangePasswordError('');
    setChangePasswordSuccess('');

    if (!newPassword.trim() || !confirmPassword.trim()) {
      setChangePasswordError('Please enter and confirm your new password.');
      return;
    }

    if (newPassword.length < 6) {
      setChangePasswordError('Password must be at least 6 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setChangePasswordError('New passwords do not match.');
      return;
    }

    setChangePasswordLoading(true);
    const result = await changePassword(newPassword);

    if (!result.success) {
      setChangePasswordError(result.error || 'Failed to change password.');
      setChangePasswordLoading(false);
      return;
    }

    setChangePasswordSuccess('Password changed successfully!');
    setNewPassword('');
    setConfirmPassword('');
    setCurrentPassword('');
    setTimeout(() => {
      setShowChangePassword(false);
      setChangePasswordSuccess('');
    }, 2000);
    setChangePasswordLoading(false);
  };

  // Show loading screen while initial data is being fetched from Supabase
  // This prevents the empty-app flash that occurs between login and data load
  if (dbLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin mx-auto"
               style={{ borderColor: '#1A5C3A', borderTopColor: 'transparent' }}></div>
          <p className="text-gray-600 text-sm mt-4 font-medium">Loading your events...</p>
          <p className="text-gray-400 text-xs mt-1">Connecting to database</p>
        </div>
      </div>
    );
  }

  // Housekeeping login (shared phone / iPad): only the housekeeping screen.
  if (role === 'housekeeping') {
    return <HousekeepingLive lockedIn onLogout={logout} />;
  }

  // Close sidebar on page change (mobile)
  const handleNavigate = (page, action) => {
    setNavigateAction(action || null);
    setActivePage(page);
    setSidebarOpen(false);
  };

  // Day views (buildDay) can hand over a display-only copy; always edit the stored event.
  const handleSelectEvent = (event) => {
    setSelectedEvent(event?._raw || event);
    setActivePage('events');
    setSidebarOpen(false);
  };

  const handleSelectEventInPage = (event, page) => {
    setSelectedEvent(event?._raw || event);
    setActivePage(page);
    setSidebarOpen(false);
  };

  // Page registry
  const pages = {
    dashboard: Dashboard,
    pipeline: Pipeline,
    diary: RoomDiary,
    events: Events,
    tables: Tables,
    functionsheets: FunctionSheets,
    kitchen: KitchenLive,
    housekeeping: HousekeepingBoard,
    hk_live: HousekeepingLive,
    billing: Billing,
    clients: Clients,
    email: Email,
    marketing: Marketing,
    integrations: Integrations,
    booking_widget: BookingWidget,
    user_management: UserManagement,
    wyw: WinYourWedding,
  };

  const navPageKeys = {
    dashboard: 'dashboard',
    pipeline: 'pipeline',
    diary: 'room_diary',
    events: 'events',
    tables: 'tables',
    functionsheets: 'function_sheets',
    kitchen: 'function_sheets',
    housekeeping: 'housekeeping',
    hk_live: 'housekeeping',
    billing: 'billing',
    clients: 'clients',
    email: 'email',
    marketing: 'marketing',
    integrations: 'integrations',
    booking_widget: 'integrations',
    user_management: 'user_management',
    wyw: 'events',
  };

  // Operations-first menu (Oct 2026). Wedding Pipeline, Marketing and Email
  // are hidden from the menu (little or no use); their pages stay registered
  // above so nothing breaks and they can be brought back by re-adding a line.
  const mainNavItems = [
    { id: 'dashboard', label: 'Today', icon: LayoutDashboard },
    { id: 'diary', label: 'Room Diary', icon: Calendar },
    { id: 'tables', label: 'Tables', icon: UtensilsCrossed },
    { id: 'functionsheets', label: 'Function Sheets', icon: FileText },
    { id: 'kitchen', label: 'Kitchen Live', icon: ChefHat },
    { id: 'housekeeping', label: 'Housekeeping', icon: BedDouble },
    { id: 'events', label: 'Events & Weddings', icon: ClipboardList },
    { id: 'wyw', label: 'Win Your Wedding', icon: Heart },
  ];

  // Back office — collapsed by default
  const officeItems = [
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'clients', label: 'Clients', icon: Users },
  ];

  const settingsItems = [
    { id: 'integrations', label: 'Integrations', icon: Link2 },
    { id: 'booking_widget', label: 'Booking Widget', icon: Link2 },
    { id: 'user_management', label: 'User Management', icon: Shield },
  ];

  const allNavItems = [...mainNavItems, ...officeItems, ...settingsItems];

  const filteredMainNav = mainNavItems.filter((item) => {
    const pageKey = navPageKeys[item.id];
    return canAccessPage(role, pageKey);
  });

  const filteredOfficeNav = officeItems.filter((item) => canAccessPage(role, navPageKeys[item.id]));
  const officeOpen = showOffice || filteredOfficeNav.some((i) => i.id === activePage);

  const filteredSettingsNav = settingsItems.filter((item) => {
    const pageKey = navPageKeys[item.id];
    return canAccessPage(role, pageKey);
  });

  const navItems = [...filteredMainNav, ...filteredOfficeNav, ...filteredSettingsNav];

  const pageKey = navPageKeys[activePage];
  if (pageKey && !canAccessPage(role, pageKey)) {
    setActivePage('dashboard');
  }

  const ActivePage = pages[activePage] || pages.dashboard;
  const unreadNotifications = (notifications || []).filter((n) => !n.read);
  const roleInfo = ROLES[role] || ROLES.reception;

  // Find current page label for mobile header
  const currentPageLabel = allNavItems.find((item) => item.id === activePage)?.label || 'Dashboard';

  return (
    <div className="flex h-screen bg-gray-50 print:h-auto print:block print:bg-white">

      {/* ── Mobile top bar ── */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-gray-900 text-white flex items-center justify-between px-4 h-14 md:hidden no-print">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-1.5 rounded-lg hover:bg-gray-800 transition"
          aria-label="Open menu"
        >
          <Menu size={22} />
        </button>
        <h1
          className="text-base font-semibold text-emerald-400 cursor-pointer truncate mx-3"
          onClick={() => { setActivePage('dashboard'); setSidebarOpen(false); }}
        >
          Errigal Events
        </h1>
        <div className="flex items-center gap-2">
          {/* Mobile notification bell */}
          <div ref={notifRef} className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="p-1.5 rounded-lg hover:bg-gray-800 transition relative"
            >
              <Bell size={20} />
              {unreadNotifications.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                  {unreadNotifications.length > 9 ? '9+' : unreadNotifications.length}
                </span>
              )}
            </button>
            {/* Mobile notification dropdown */}
            {showNotifications && (
              <div className="absolute right-0 top-full mt-2 w-80 bg-gray-800 rounded-lg shadow-xl border border-gray-700 max-h-96 overflow-y-auto thin-scrollbar z-50">
                <div className="flex items-center justify-between px-3 py-2 border-b border-gray-700">
                  <span className="text-xs font-semibold text-gray-300">Notifications</span>
                  {(notifications || []).length > 0 && (
                    <button onClick={clearAllNotifications} className="text-[10px] text-gray-500 hover:text-red-400 transition">Clear all</button>
                  )}
                </div>
                {(notifications || []).length === 0 ? (
                  <div className="flex flex-col items-center py-6 text-gray-500">
                    <Inbox size={24} className="mb-2 opacity-50" />
                    <p className="text-xs">No notifications</p>
                  </div>
                ) : (
                  <div>
                    {(notifications || []).slice(0, 15).map((notif, i) => {
                      const isTask = notif.type === 'task_assigned';
                      const iconColor = isTask ? 'text-emerald-400' : notif.type === 'stage_change' ? 'text-blue-400' : notif.type === 'new_enquiry' ? 'text-purple-400' : 'text-yellow-400';
                      return (
                        <div
                          key={notif.id || i}
                          className={`px-3 py-2.5 border-b border-gray-700/50 last:border-0 group transition ${!notif.read ? 'bg-gray-700/40' : 'hover:bg-gray-700/20'}`}
                          onClick={() => dismissNotification(notif.id)}
                        >
                          <div className="flex items-start gap-2">
                            <div className={`mt-0.5 flex-shrink-0 ${iconColor}`}>
                              {isTask ? <ListTodo size={14} /> : <Bell size={14} />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-xs leading-relaxed ${!notif.read ? 'text-white font-medium' : 'text-gray-400'}`}>
                                {notif.title}
                              </p>
                              {notif.message && notif.message !== notif.title && (
                                <p className="text-[11px] text-gray-500 mt-0.5 truncate">{notif.message}</p>
                              )}
                              {notif.time && <p className="text-[10px] text-gray-600 mt-1">{notif.time}</p>}
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); deleteNotification(notif.id); }}
                              className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 transition flex-shrink-0 mt-0.5"
                              title="Delete"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Mobile overlay ── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Sidebar ── */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-gray-900 text-white overflow-y-auto thin-scrollbar flex flex-col
          transform transition-transform duration-200 ease-in-out
          md:static md:translate-x-0 md:w-56 md:shrink-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          no-print
        `}
      >
        {/* Logo + Close button (mobile) */}
        <div className="p-4 border-b border-gray-800">
          <div className="flex items-center justify-between mb-3">
            <h1
              className="text-xl font-bold text-emerald-400 cursor-pointer"
              onClick={() => { setActivePage('dashboard'); setSidebarOpen(false); }}
            >
              Errigal Events
            </h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-1 rounded-lg hover:bg-gray-800 transition md:hidden"
              aria-label="Close menu"
            >
              <X size={20} className="text-gray-400" />
            </button>
          </div>

          {/* Profile Area */}
          <div ref={profileRef} className="relative">
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-800 transition"
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                style={{ backgroundColor: getRoleColour(role) }}
              >
                {currentUser?.initials || '?'}
              </div>
              <div className="flex-1 text-left min-w-0">
                <p className="text-sm font-medium text-white truncate">{currentUser?.name || 'User'}</p>
                <p className="text-xs truncate" style={{ color: roleInfo.colour }}>{roleInfo.label}</p>
              </div>
              <ChevronDown size={14} className="text-gray-400 flex-shrink-0" />
            </button>

            {/* Profile Dropdown */}
            {showProfileMenu && (
              <div className="absolute left-0 right-0 top-full mt-1 bg-gray-800 rounded-lg shadow-xl border border-gray-700 z-50 overflow-hidden">
                <div className="py-1">
                  {!isRealAuth && (availableUsers || []).map((user) => (
                    <button
                      key={user.id}
                      onClick={() => {
                        switchUser(user.id);
                        setShowProfileMenu(false);
                        setActivePage('dashboard');
                        setSidebarOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-700 transition"
                    >
                      <div
                        className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                        style={{ backgroundColor: getRoleColour(user.role) }}
                      >
                        {user.initials}
                      </div>
                      <div className="flex-1 text-left">
                        <p className="text-sm text-white">{user.name}</p>
                        <p className="text-xs" style={{ color: ROLES[user.role]?.colour }}>{ROLES[user.role]?.label}</p>
                      </div>
                      {currentUser?.id === user.id && (
                        <Check size={14} className="text-emerald-400" />
                      )}
                    </button>
                  ))}
                  {isRealAuth && (
                    <>
                      <button
                        onClick={() => {
                          setShowChangePassword(true);
                          setShowProfileMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-700 transition text-gray-300"
                      >
                        <Lock size={16} />
                        <span className="text-sm">Change Password</span>
                      </button>
                      <button
                        onClick={() => { logout(); setShowProfileMenu(false); setSidebarOpen(false); }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-700 transition text-red-400 border-t border-gray-700"
                      >
                        <LogOut size={16} />
                        <span className="text-sm">Sign out</span>
                      </button>
                    </>
                  )}
                  {!isRealAuth && (
                    <div className="px-3 py-2 border-t border-gray-700">
                      <p className="text-xs text-gray-500">Demo mode — switch profiles above</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Notifications Bell — desktop only (mobile has it in top bar) */}
        <div className="px-4 pt-3 hidden md:block" ref={notifRef}>
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-gray-300 hover:bg-gray-800 transition relative"
          >
            <Bell size={18} />
            <span className="text-sm font-medium">Notifications</span>
            {unreadNotifications.length > 0 && (
              <span className="ml-auto bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {unreadNotifications.length > 9 ? '9+' : unreadNotifications.length}
              </span>
            )}
          </button>

          {showNotifications && (
            <div className="mt-1 bg-gray-800 rounded-lg shadow-xl border border-gray-700 max-h-96 overflow-y-auto thin-scrollbar">
              <div className="flex items-center justify-between px-3 py-2 border-b border-gray-700 sticky top-0 bg-gray-800 z-10">
                <span className="text-xs font-semibold text-gray-300">Notifications</span>
                {(notifications || []).length > 0 && (
                  <button onClick={clearAllNotifications} className="text-[10px] text-gray-500 hover:text-red-400 transition">Clear all</button>
                )}
              </div>
              {(notifications || []).length === 0 ? (
                <div className="flex flex-col items-center py-6 text-gray-500">
                  <Inbox size={24} className="mb-2 opacity-50" />
                  <p className="text-xs">No notifications</p>
                </div>
              ) : (
                <div>
                  {(notifications || []).slice(0, 15).map((notif, i) => {
                    const isTask = notif.type === 'task_assigned';
                    const iconColor = isTask ? 'text-emerald-400' : notif.type === 'stage_change' ? 'text-blue-400' : notif.type === 'new_enquiry' ? 'text-purple-400' : 'text-yellow-400';
                    return (
                      <div
                        key={notif.id || i}
                        className={`px-3 py-2.5 border-b border-gray-700/50 last:border-0 group transition cursor-pointer ${!notif.read ? 'bg-gray-700/40' : 'hover:bg-gray-700/20'}`}
                        onClick={() => dismissNotification(notif.id)}
                      >
                        <div className="flex items-start gap-2">
                          <div className={`mt-0.5 flex-shrink-0 ${iconColor}`}>
                            {isTask ? <ListTodo size={14} /> : <Bell size={14} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs leading-relaxed ${!notif.read ? 'text-white font-medium' : 'text-gray-400'}`}>
                              {notif.title}
                            </p>
                            {notif.message && notif.message !== notif.title && (
                              <p className="text-[11px] text-gray-500 mt-0.5 truncate">{notif.message}</p>
                            )}
                            {notif.time && <p className="text-[10px] text-gray-600 mt-1">{notif.time}</p>}
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteNotification(notif.id); }}
                            className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 transition flex-shrink-0 mt-0.5"
                            title="Delete"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-4 space-y-1">
          {/* Main Navigation Items */}
          {filteredMainNav.map((item) => {
            const Icon = item.icon;
            const isActive = activePage === item.id;

            return (
              <button
                key={item.id}
                onClick={() => { setActivePage(item.id); setSelectedEvent(null); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-emerald-600 text-white'
                    : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <Icon size={18} />
                <span className="text-sm font-medium">{item.label}</span>
                {item.id === 'email' && unreadEmailCount > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1">
                    {unreadEmailCount > 9 ? '9+' : unreadEmailCount}
                  </span>
                )}
              </button>
            );
          })}

          {/* Office Section (back office, collapsed) */}
          {filteredOfficeNav.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-700">
              <button
                onClick={() => setShowOffice(!showOffice)}
                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-gray-300 hover:bg-gray-800 transition-colors"
              >
                <Briefcase size={18} />
                <span className="text-sm font-medium flex-1 text-left">Office</span>
                <ChevronDown size={14} className={`transition-transform ${officeOpen ? 'rotate-180' : ''}`} />
              </button>
              {officeOpen && (
                <div className="mt-1 ml-2 space-y-1 border-l border-gray-700 pl-2">
                  {filteredOfficeNav.map((item) => {
                    const Icon = item.icon;
                    const isActive = activePage === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => { setActivePage(item.id); setSelectedEvent(null); setSidebarOpen(false); }}
                        className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg text-sm transition-colors ${
                          isActive ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:bg-gray-800 hover:text-gray-300'
                        }`}
                      >
                        <Icon size={16} />
                        <span className="font-medium">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Settings Section */}
          {filteredSettingsNav.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-700">
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-gray-300 hover:bg-gray-800 transition-colors"
              >
                <Settings size={18} />
                <span className="text-sm font-medium flex-1 text-left">Settings</span>
                <ChevronDown
                  size={14}
                  className={`transition-transform ${showSettings ? 'rotate-180' : ''}`}
                />
              </button>

              {/* Settings Items (collapsible) */}
              {showSettings && (
                <div className="mt-1 ml-2 space-y-1 border-l border-gray-700 pl-2">
                  {filteredSettingsNav.map((item) => {
                    const Icon = item.icon;
                    const isActive = activePage === item.id;

                    return (
                      <button
                        key={item.id}
                        onClick={() => { setActivePage(item.id); setSelectedEvent(null); setSidebarOpen(false); }}
                        className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg text-sm transition-colors ${
                          isActive
                            ? 'bg-emerald-600 text-white'
                            : 'text-gray-400 hover:bg-gray-800 hover:text-gray-300'
                        }`}
                      >
                        <Icon size={16} />
                        <span className="font-medium">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </nav>

        {/* Footer: Integration Status */}
        <div className="border-t border-gray-800 p-4 space-y-2">
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-800 rounded-lg">
            <AlertCircle size={14} className="text-amber-500" />
            <span className="text-xs text-gray-300">
              <span className="text-amber-500 font-medium">Tevalis POS</span> Pending
            </span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-800 rounded-lg">
            <Clock size={14} className="text-gray-500" />
            <span className="text-xs text-gray-300">
              <span className="text-gray-400 font-medium">Clock PMS</span> Planned
            </span>
          </div>
        </div>
      </aside>

      {/* ── Content Area ── */}
      <main className="flex-1 overflow-auto bg-gray-50 pt-14 md:pt-0 print:overflow-visible print:bg-white">
        {ActivePage && (
          <ActivePage
            onNavigate={handleNavigate}
            onSelectEvent={handleSelectEvent}
            onSelectEventInPage={handleSelectEventInPage}
            selectedEvent={selectedEvent}
            navigateAction={navigateAction}
            clearNavigateAction={() => setNavigateAction(null)}
          />
        )}
      </main>

      {/* Email workflow toast notifications */}
      <ToastContainer
        toasts={emailToasts || []}
        onDismiss={dismissToast}
        onCancel={cancelWorkflowEmail}
      />

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl border border-gray-200 max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Change Password</h2>

            {changePasswordError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{changePasswordError}</p>
              </div>
            )}

            {changePasswordSuccess && (
              <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start gap-2">
                <Check size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-emerald-700">{changePasswordSuccess}</p>
              </div>
            )}

            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Min 6 characters"
                    autoFocus
                    className="w-full px-4 py-2.5 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    className="w-full px-4 py-2.5 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={changePasswordLoading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-60 transition"
                >
                  {changePasswordLoading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Lock size={16} />
                      Update Password
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowChangePassword(false);
                    setNewPassword('');
                    setConfirmPassword('');
                    setCurrentPassword('');
                    setChangePasswordError('');
                    setChangePasswordSuccess('');
                  }}
                  className="flex-1 px-4 py-2.5 text-gray-600 font-medium rounded-lg hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
